import React, { useState } from 'react';
import { FaCheck, FaClock, FaSun, FaGraduationCap, FaBriefcase, FaHeart, FaPlus } from 'react-icons/fa';
import styled from 'styled-components';

const TemplatesContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin: 20px 0;
`;

const TemplatesHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const TemplatesTitle = styled.h2`
  color: #2d3748;
  font-size: 1.8rem;
  margin-bottom: 10px;
`;

const TemplatesSubtitle = styled.p`
  color: #718096;
  font-size: 1rem;
  margin: 0;
`;

const TemplatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TemplateCard = styled.div`
  border: 2px solid ${props => props.selected ? '#0078D4' : '#e2e8f0'};
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.selected ? '#f7fafc' : 'white'};
  
  &:hover {
    border-color: #0078D4;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 120, 212, 0.15);
  }
`;

const TemplateHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
`;

const TemplateIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${props => props.color || '#0078D4'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
`;

const TemplateInfo = styled.div`
  flex: 1;
`;

const TemplateName = styled.h3`
  color: #2d3748;
  font-size: 1.2rem;
  margin: 0 0 5px 0;
`;

const TemplateDuration = styled.p`
  color: #718096;
  font-size: 0.9rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const TemplateDescription = styled.p`
  color: #4a5568;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 15px;
`;

const TaskList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TaskItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  color: #4a5568;
  font-size: 0.9rem;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
  }
`;

const TaskIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  color: #718096;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 30px;
`;

const ActionButton = styled.button`
  background: ${props => props.primary ? '#0078D4' : 'transparent'};
  color: ${props => props.primary ? 'white' : '#0078D4'};
  border: 2px solid #0078D4;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.primary ? '#005a9e' : '#f7fafc'};
    transform: translateY(-1px);
  }
`;

const CustomTemplateCard = styled.div`
  border: 2px dashed #cbd5e0;
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8f9fa;
  
  &:hover {
    border-color: #0078D4;
    background: #f7fafc;
  }
`;

const CustomTemplateIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  color: #718096;
  font-size: 1.5rem;
`;

export const STARTER_TEMPLATES = [
  {
    id: 'morning-routine',
    name: 'Morning Routine',
    icon: <FaSun />,
    color: '#f6ad55',
    duration: '30-45 min',
    description: 'Start your day with energy and purpose. Perfect for establishing healthy habits.',
    tasks: [
      'Drink a glass of water',
      'Make your bed',
      '5-minute meditation',
      'Write 3 things you\'re grateful for',
      'Plan your top 3 priorities for the day',
      'Healthy breakfast',
      'Review your calendar'
    ]
  },
  {
    id: 'study-plan',
    name: 'Study Plan',
    icon: <FaGraduationCap />,
    color: '#4299e1',
    duration: '2-3 hours',
    description: 'Structured study session for students. Optimized for focus and retention.',
    tasks: [
      'Review previous notes',
      'Read new material (45 min)',
      'Take practice quiz',
      'Create summary notes',
      'Review flashcards',
      'Plan next study session',
      'Update study schedule'
    ]
  },
  {
    id: 'workday-flow',
    name: 'Workday Flow',
    icon: <FaBriefcase />,
    color: '#48bb78',
    duration: '8 hours',
    description: 'Professional workflow for maximum productivity and work-life balance.',
    tasks: [
      'Check emails and prioritize',
      'Review daily goals',
      'Deep work session (2 hours)',
      'Team check-in meeting',
      'Lunch break',
      'Administrative tasks',
      'Project planning',
      'End-of-day review'
    ]
  },
  {
    id: 'self-care',
    name: 'Self-Care Sunday',
    icon: <FaHeart />,
    color: '#ed64a6',
    duration: '2-4 hours',
    description: 'Weekly self-care routine to recharge and prepare for the week ahead.',
    tasks: [
      'Sleep in and rest',
      'Healthy breakfast',
      'Exercise or yoga',
      'Read for pleasure',
      'Connect with loved ones',
      'Plan upcoming week',
      'Prepare for Monday',
      'Evening relaxation'
    ]
  }
];

const StarterTemplates = ({ onSelectTemplate, onCreateCustom, title = "Choose Your Starter Template", subtitle = "Get started quickly with pre-made task lists designed for common productivity goals" }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
  };

  const handleUseTemplate = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate);
    }
  };

  return (
    <TemplatesContainer>
      <TemplatesHeader>
        <TemplatesTitle>{title}</TemplatesTitle>
        <TemplatesSubtitle>{subtitle}</TemplatesSubtitle>
      </TemplatesHeader>

      <TemplatesGrid>
        {STARTER_TEMPLATES.map((template) => (
          <TemplateCard
            key={template.id}
            selected={selectedTemplate?.id === template.id}
            onClick={() => handleSelectTemplate(template)}
          >
            <TemplateHeader>
              <TemplateIcon color={template.color}>
                {template.icon}
              </TemplateIcon>
              <TemplateInfo>
                <TemplateName>{template.name}</TemplateName>
                <TemplateDuration>
                  <FaClock />
                  {template.duration}
                </TemplateDuration>
              </TemplateInfo>
            </TemplateHeader>
            
            <TemplateDescription>{template.description}</TemplateDescription>
            
            <TaskList>
              {template.tasks.slice(0, 4).map((task, index) => (
                <TaskItem key={index}>
                  <TaskIcon>{index + 1}</TaskIcon>
                  {task}
                </TaskItem>
              ))}
              {template.tasks.length > 4 && (
                <TaskItem>
                  <TaskIcon>+</TaskIcon>
                  +{template.tasks.length - 4} more tasks
                </TaskItem>
              )}
            </TaskList>
          </TemplateCard>
        ))}
        
        <CustomTemplateCard onClick={onCreateCustom}>
          <CustomTemplateIcon>
            <FaPlus />
          </CustomTemplateIcon>
          <h3 style={{ color: '#2d3748', margin: '0 0 10px 0' }}>Create Custom</h3>
          <p style={{ color: '#718096', margin: 0 }}>
            Start from scratch with your own personalized task list
          </p>
        </CustomTemplateCard>
      </TemplatesGrid>

      <ActionButtons>
        <ActionButton onClick={onCreateCustom}>
          <FaPlus />
          Create Custom List
        </ActionButton>
        {selectedTemplate && (
          <ActionButton primary onClick={handleUseTemplate}>
            <FaCheck />
            Use {selectedTemplate.name}
          </ActionButton>
        )}
      </ActionButtons>
    </TemplatesContainer>
  );
};

export default StarterTemplates;
